import { Component, OnDestroy, OnInit } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';
import { keysToTranslate } from 'i18n/keys-to-translate';
import {
  PersonAdresseListEntryMeta,
  PersonStammMasterDetailEntryMeta,
  PersonStammMasterId,
  PersonstammpersonService,
  PersonStammVersionId
} from '../../../core/personstamm-api';
import { LinkComponent, NavTarget } from '../../../shared/components/link/link.component';
import { AppPermissionService } from '../../../core/services/app-permission.service';
import { TimepointService } from '../../../core/services/timepoint.service';
import { combineLatest, Subscription } from 'rxjs';
import { AppConfigService } from '../../../core/services/app-config.service';
import { map } from 'rxjs/operators';
import { FormatConfigService } from '../../../core/services/format-config.service';
import { ListLoader } from '../../../shared/components/list-loader/list-loader';
import { ListLoaderPreloaded } from '../../../shared/components/list-loader/list-loader-preloaded';
import { HistoryMode } from '../../../core/services/HistoryMode';
import { ContentHeaderComponent } from '../../../shared/components/content-header/content-header.component';
import { TitleComponent } from '../../../shared/components/title/title.component';
import { KompaktStammpersonComponent } from '../../../shared/kompaktdarstellung/stammperson/kompakt-stammperson.component';
import { TitleFunctionbarComponent } from '../../../shared/components/title-functionbar/title-functionbar.component';
import { AuszugComponent } from '../../../shared/components/auszug/auszug.component';
import { RubrikComponent } from '../../../shared/components/rubrik/rubrik.component';
import { PersonstammNatuerlichePersonComponent } from './natuerliche-person/personstamm-natuerliche-person.component';
import { PersonstammSchweizJuristischePersonComponent } from './schweiz-juristische-person/personstamm-schweiz-juristische-person.component';
import { PersonstammOeffentlRechtlKoerperschaftComponent } from './oeffentl-rechtl-koerperschaft/personstamm-oeffentl-rechtl-koerperschaft.component';
import { PersonstammAuslaendischeRechtsformComponent } from './auslaendische-rechtsform/personstamm-auslaendischerechtsform.component';
import { PersonstammAdressenComponent } from './adressen/personstamm-adressen.component';
import { NgbNav, NgbNavContent, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase, NgbNavOutlet } from '@ng-bootstrap/ng-bootstrap';
import { PersonstammEigentumComponent } from './eigentum/personstamm-eigentum.component';
import { PersonstammUebrigeBerechtigungenComponent } from './uebrige-berechtigungen/personstamm-uebrige-berechtigungen.component';
import { PersonstammGlaeubigerComponent } from './glaeubiger/personstamm-glaeubiger.component';
import { PersonstammSchuldnerComponent } from './schuldner/personstamm-schuldner.component';
import { CapSpinnerSmallComponent } from '../../../shared/components/cap-spinner-small/cap-spinner-small.component';
import { TooltipPersonstammPipe } from '../../../shared/pipes/simple/tooltip-personstamm.pipe';
import { PermissionResponse } from '../../../core/base-api';
import PermissionsEnum = PermissionResponse.PermissionsEnum;

@Component({
  selector: 'cap-personstamm-detail',
  templateUrl: './personstamm-detail.component.html',
  styleUrls: ['./personstamm-detail.component.scss'],
  imports: [
    ContentHeaderComponent,
    TitleComponent,
    LinkComponent,
    KompaktStammpersonComponent,
    TitleFunctionbarComponent,
    AuszugComponent,
    RubrikComponent,
    PersonstammNatuerlichePersonComponent,
    PersonstammSchweizJuristischePersonComponent,
    PersonstammOeffentlRechtlKoerperschaftComponent,
    PersonstammAuslaendischeRechtsformComponent,
    PersonstammAdressenComponent,
    NgbNav,
    NgbNavItem,
    NgbNavItemRole,
    NgbNavLink,
    NgbNavLinkBase,
    NgbNavContent,
    PersonstammEigentumComponent,
    TranslatePipe,
    PersonstammUebrigeBerechtigungenComponent,
    PersonstammGlaeubigerComponent,
    PersonstammSchuldnerComponent,
    NgbNavOutlet,
    CapSpinnerSmallComponent,
    TooltipPersonstammPipe
  ]
})
export class PersonstammDetailComponent implements OnInit, OnDestroy {
  navTargets = NavTarget;

  historisiert: boolean;
  personStammMasterId: PersonStammMasterId;
  personStammVersionId: PersonStammVersionId;
  egbpid: string;
  adressen: ListLoader<PersonAdresseListEntryMeta>;
  historyMode: HistoryMode.HistoryMode;
  public entry: PersonStammMasterDetailEntryMeta;
  public showEigentum: boolean;
  public showUebrigeBerechtigungen: boolean;
  public showGlaeubigerSchuldner: boolean;
  public showAlleRollen: boolean;
  hasPermissionAuskunft: boolean = false;
  hasPermissionAdresse: boolean = false;

  anzeigeNichtOeffentlicheAnmerkungen: boolean;

  subscription: Subscription = new Subscription();

  constructor(
    private route: ActivatedRoute,
    public translate: TranslateService,
    private personstammService: PersonstammpersonService,
    private permissionService: AppPermissionService,
    private timepointService: TimepointService,
    private appConfigService: AppConfigService,
    private formatConfig: FormatConfigService
  ) {
    keysToTranslate<string>([]);
    const currentTypePath = route.snapshot.url[0].path;
    this.showAlleRollen = LinkComponent.getPath(NavTarget.NAV_PERSON_STAMM).indexOf(currentTypePath) > 0;
    this.showEigentum =
      (LinkComponent.getPath(NavTarget.NAV_PERSON_EIGENTUEMER_VERHAELTNISSE).indexOf(currentTypePath) > 0 || this.showAlleRollen) &&
      this.permissionService.hasAllPermissions(['ANZEIGE_EIGENTUM', 'EIGENTUEMER_REGISTER']);
    this.showUebrigeBerechtigungen =
      (LinkComponent.getPath(NavTarget.NAV_PERSON_UEBRIGEBERECHTIGTE).indexOf(currentTypePath) > 0 || this.showAlleRollen) &&
      this.hasUebrigeberechtigtePermission();
    this.showGlaeubigerSchuldner =
      (LinkComponent.getPath(NavTarget.NAV_PERSON_GLAEUBIGERAUSZUG).indexOf(currentTypePath) > 0 || this.showAlleRollen) &&
      this.permissionService.hasAllPermissions(['ANZEIGE_PFANDRECHTE', 'GLAEUBIGER_REGISTER']);
    this.anzeigeNichtOeffentlicheAnmerkungen = this.permissionService.hasPermission('ANZEIGE_NICHT_OEFFENTLICHE_ANMERKUNGEN');
    this.hasPermissionAuskunft = this.permissionService.hasPermission(PermissionsEnum.AUSKUNFTSPERRE);
    this.hasPermissionAdresse = this.permissionService.hasPermission(PermissionsEnum.ADRESSSPERRE);
  }

  get showEintragungenImGrundbuch() {
    return this.showEigentum || this.showGlaeubigerSchuldner || this.showUebrigeBerechtigungen || this.showAlleRollen;
  }

  ngOnInit(): void {
    this.subscription.add(
      combineLatest([this.route.params, this.route.queryParams])
        .pipe(map((results) => ({ params: results[0], query: results[1] })))
        .subscribe((params) => {
          this.personStammVersionId = <PersonStammVersionId>{ id: params.params['versionId'] };
          this.personStammMasterId = <PersonStammMasterId>{ id: params.params['id'] };
          this.egbpid = params.query['egbpid'];
          this.loadPersonstamm();
        })
    );
    this.subscription.add(
      this.appConfigService.einstellungHistorischeDaten$.subscribe((newHistMode) => {
        if (this.historyMode && this.historyMode === newHistMode) {
          return;
        }
        this.loadPersonstamm();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  hasAdresssperre(auskunftsperre: PersonStammMasterDetailEntryMeta.AuskunftsperreEnum): boolean {
    return auskunftsperre === PersonStammMasterDetailEntryMeta.AuskunftsperreEnum.ADRESS_SPERRE;
  }

  hasAuskunftsperre(auskunftsperre: PersonStammMasterDetailEntryMeta.AuskunftsperreEnum): boolean {
    return auskunftsperre === PersonStammMasterDetailEntryMeta.AuskunftsperreEnum.AUSKUNFT_SPERRE;
  }

  showPersonendaten(auskunftsperre: PersonStammMasterDetailEntryMeta.AuskunftsperreEnum): boolean {
    let hasAuskunftsperre = this.hasAuskunftsperre(auskunftsperre);
    return !hasAuskunftsperre || (hasAuskunftsperre && this.hasPermissionAuskunft);
  }

  private loadPersonstamm() {
    if (this.personStammMasterId && this.personStammMasterId.id) {
      this.historyMode = this.appConfigService.einstellungHistorischeDaten;
      this.subscription.add(
        this.personstammService
          .loadById(
            this.personStammMasterId.id,
            this.appConfigService.einstellungHistorischeDaten,
            this.personStammVersionId.id,
            this.timepointService.timepointCtx.timepointIso,
            null
          )
          .subscribe((entry) => {
            this.entry = entry;
            this.adressen = new ListLoaderPreloaded<PersonAdresseListEntryMeta>(15, 1, entry.adressen);
          })
      );
    } else if (this.egbpid) {
      this.subscription.add(
        this.personstammService
          .getPersonstammDetailByFachId(
            this.egbpid,
            this.appConfigService.einstellungHistorischeDaten,
            this.timepointService.timepointCtx.timepointIso,
            null
          )
          .subscribe((entry) => {
            this.personStammMasterId = <PersonStammMasterId>{ id: entry.masterId.id };
            this.entry = entry;
            this.adressen = new ListLoaderPreloaded<PersonAdresseListEntryMeta>(15, 1, entry.adressen);
          })
      );
    } else {
      return;
    }
  }

  private hasUebrigeberechtigtePermission() {
    return (
      this.permissionService.hasPermission('UEBRIGE_BERECHTIGTE_REGISTER') &&
      (this.permissionService.hasPermission('ANZEIGE_ANMERKUNGEN') ||
        this.permissionService.hasPermission('ANZEIGE_DIENSTBARKEITEN') ||
        this.permissionService.hasPermission('ANZEIGE_GRUNDLASTEN') ||
        this.permissionService.hasPermission('ANZEIGE_VORMERKUNGEN'))
    );
  }
}
